import { type PayloadAction, createSlice } from '@reduxjs/toolkit'

export interface AdminUser {
  id: string
  email: string
  username: string | null
  first_name: string
  last_name: string
  full_name: string
  phone_number?: string | null
  level: string
  display_name: string

  // Status & Security
  is_active: boolean
  is_superuser: boolean
  is_two_factor_enabled: boolean
  force_password_change: boolean
  status: 'active' | 'inactive' | 'suspended'
  is_2fa_enabled: boolean
  is_email_verified: boolean
  failed_login_attempts: number
  account_locked_until?: string | null

  // Role Information
  role: string

  role_detail: {
    id: string
    name: string
    description: string
    permission_count: number
    is_system: boolean
    created_at: string
    updated_at: string
    level: string
    display_name: string
  } | null
  permissions: string[]

  // Location/Tenant Information
  organization: string | null
  country: string | null
  region: string | null
  zone: string | null
  branch: string | null
  location_name: string | null

  tenant_info: {
    id: string
    branch: any
    zone: any
    region: any
    country: any
    type: any
    tenant_level: string | null
    organization_id: string | null
    organization_slug: string | null
    organization_name: string | null
    country_id: string | null
    country_slug: string | null
    country_name: string | null
    region_id: string | null
    region_slug: string | null
    region_name: string | null
    zone_id: string | null
    zone_slug: string | null
    zone_name: string | null
    branch_id: string | null
    branch_slug: string | null
    branch_name: string | null
  } | null

  // Timestamps
  date_joined: string
  last_login: string | null
  created_at: string
  updated_at: string
  created_by?: string | undefined
}

export interface AdminUserFilters {
  location_id: any
  is_active: undefined
  is_superuser: undefined
  is_2fa_enabled: undefined
  page_size: any
  ordering: any
  country: any
  region: any
  zone: any
  page: number
  pageSize: number
  search: string
  status: 'all' | 'active' | 'inactive' | 'suspended'
  role: string // 'all' or role ID
  branch: string // 'all' or branch ID
  dateFrom: Date | null
  dateTo: Date | null
  sortBy: string
  sortOrder: 'asc' | 'desc'
  additional:
    | 'none'
    | '2fa_enabled'
    | '2fa_disabled'
    | 'email_verified'
    | 'email_unverified'
    | 'recently_active'
    | 'never_logged_in'
}

export interface AdminUserStatistics {
  totalUsers: number
  activeUsers: number
  suspendedUsers: number
  twoFactorEnabled: number
  verifiedUsers: number
  totalBranches: number
  userGrowth: number
  activeGrowth: number
  suspendedChange: number
  newUsersToday: number
  newUsersThisWeek: number
}

export interface AdminUserPagination {
  total: number
  page: number
  pageSize: number
  totalPages: number
  hasNext: boolean
  hasPrevious: boolean
}

export interface CreateAdminUserData {
  email: string
  first_name: string
  last_name: string
  phone_number?: string
  role_id: string
  branch_id?: string
  send_invite: boolean
}

export interface UpdateAdminUserData {
  first_name?: string
  last_name?: string
  phone_number?: string
  role_id?: string
  branch_id?: string
  is_active?: boolean
}

export interface BulkAdminUserOperation {
  operation:
    | 'activate'
    | 'suspend'
    | 'delete'
    | 'reset_password'
    | 'assign_role'
    | 'assign_branch'
    | 'assign_tenant'
    | 'send_email'
    | 'deactivate'
  userIds: string[]
  params?: {
    role_id?: string
    branch_id?: string
    tenant_id?: string
    tenancy_level?: string
    email_type?: string
    reason?: string
  }
}

interface AdminUserManagementState {
  // Data
  users: AdminUser[]
  statistics: AdminUserStatistics | null

  // Filters & Pagination
  filters: AdminUserFilters
  pagination: AdminUserPagination | null

  // UI State
  loading: boolean
  error: string | null
  selectedUsers: string[]

  // Modal/Dialog State
  showCreateDialog: boolean
  showEditDialog: boolean
  showDeleteDialog: boolean
  showBulkDialog: boolean
  showDetailsDrawer: boolean
  showFiltersDrawer: boolean
  showExportDialog: boolean

  // Selected Items
  selectedUser: AdminUser | null
  editingUser: AdminUser | null

  // Last Operations
  lastRefresh: string | null
  lastOperation: string | null

  // Cache management
  cacheTimestamp: number
  autoRefresh: boolean
}

const initialFilters: AdminUserFilters = {
  page: 1,
  pageSize: 25,
  search: '',
  status: 'all',
  role: 'all',
  branch: 'all',
  dateFrom: null,
  dateTo: null,
  sortBy: 'created_at',
  sortOrder: 'desc',
  additional: 'none',
  location_id: undefined,
  is_active: undefined,
  is_superuser: undefined,
  is_2fa_enabled: undefined,
  page_size: undefined,
  ordering: undefined,
  country: undefined,
  region: undefined,
  zone: undefined,
}

const initialState: AdminUserManagementState = {
  // Data
  users: [],
  statistics: null,

  // Filters & Pagination
  filters: initialFilters,
  pagination: null,

  // UI State
  loading: false,
  error: null,
  selectedUsers: [],

  // Modal/Dialog State
  showCreateDialog: false,
  showEditDialog: false,
  showDeleteDialog: false,
  showBulkDialog: false,
  showDetailsDrawer: false,
  showFiltersDrawer: false,
  showExportDialog: false,

  // Selected Items
  selectedUser: null,
  editingUser: null,

  // Last Operations
  lastRefresh: null,
  lastOperation: null,

  // Cache management
  cacheTimestamp: 0,
  autoRefresh: true,
}

const adminUserManagementSlice = createSlice({
  name: 'adminUserManagement',
  initialState,
  reducers: {
    // Data Management
    setUsers: (state, action: PayloadAction<AdminUser[]>) => {
      state.users = action.payload
      state.loading = false
      state.error = null
      state.lastRefresh = new Date().toISOString()
    },

    setStatistics: (state, action: PayloadAction<AdminUserStatistics>) => {
      state.statistics = action.payload
    },

    setPagination: (state, action: PayloadAction<AdminUserPagination>) => {
      state.pagination = action.payload
    },

    // Loading & Error States
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload
      if (action.payload) {
        state.error = null
      }
    },

    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload
      state.loading = false
    },

    clearError: (state) => {
      state.error = null
    },

    // Filters Management
    setFilters: (state, action: PayloadAction<Partial<AdminUserFilters>>) => {
      state.filters = { ...state.filters, ...action.payload }
      // Reset page when filters change (except pagination changes)
      if (!('page' in action.payload)) {
        state.filters.page = 1
      }
    },

    resetFilters: (state) => {
      state.filters = initialFilters
      state.selectedUsers = []
    },

    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.filters.search = action.payload
      state.filters.page = 1
    },

    setSortConfig: (
      state,
      action: PayloadAction<{ sortBy: string; sortOrder: 'asc' | 'desc' }>
    ) => {
      state.filters.sortBy = action.payload.sortBy
      state.filters.sortOrder = action.payload.sortOrder
      state.filters.page = 1
    },

    // Selection Management
    setSelectedUsers: (state, action: PayloadAction<string[]>) => {
      state.selectedUsers = action.payload
    },

    toggleUserSelection: (state, action: PayloadAction<string>) => {
      const userId = action.payload
      const index = state.selectedUsers.indexOf(userId)
      if (index >= 0) {
        state.selectedUsers.splice(index, 1)
      } else {
        state.selectedUsers.push(userId)
      }
    },

    selectAllUsers: (state) => {
      state.selectedUsers = state.users.map((user) => user.id)
    },

    clearSelection: (state) => {
      state.selectedUsers = []
    },

    // Modal/Dialog Management
    openCreateDialog: (state) => {
      state.showCreateDialog = true
      state.editingUser = null
    },

    closeCreateDialog: (state) => {
      state.showCreateDialog = false
      state.editingUser = null
    },

    openEditDialog: (state, action: PayloadAction<AdminUser>) => {
      state.showEditDialog = true
      state.editingUser = action.payload
    },

    closeEditDialog: (state) => {
      state.showEditDialog = false
      state.editingUser = null
    },

    openDeleteDialog: (state, action: PayloadAction<AdminUser>) => {
      state.showDeleteDialog = true
      state.selectedUser = action.payload
    },

    closeDeleteDialog: (state) => {
      state.showDeleteDialog = false
      state.selectedUser = null
    },

    openBulkDialog: (state) => {
      state.showBulkDialog = true
    },

    closeBulkDialog: (state) => {
      state.showBulkDialog = false
    },

    openDetailsDrawer: (state, action: PayloadAction<AdminUser>) => {
      state.showDetailsDrawer = true
      state.selectedUser = action.payload
    },

    closeDetailsDrawer: (state) => {
      state.showDetailsDrawer = false
      state.selectedUser = null
    },

    openFiltersDrawer: (state) => {
      state.showFiltersDrawer = true
    },

    closeFiltersDrawer: (state) => {
      state.showFiltersDrawer = false
    },

    openExportDialog: (state) => {
      state.showExportDialog = true
    },

    closeExportDialog: (state) => {
      state.showExportDialog = false
    },

    // Data Operations
    addUser: (state, action: PayloadAction<AdminUser>) => {
      state.users.unshift(action.payload)
      state.lastOperation = 'create'
      // Update statistics
      if (state.statistics) {
        state.statistics.totalUsers += 1
        if (action.payload.status === 'active') {
          state.statistics.activeUsers += 1
        }
      }
    },

    updateUser: (state, action: PayloadAction<AdminUser>) => {
      const index = state.users.findIndex((user) => user.id === action.payload.id)
      if (index >= 0) {
        state.users[index] = action.payload
        state.lastOperation = 'update'
      }
      // Update selected user if it's the same
      if (state.selectedUser?.id === action.payload.id) {
        state.selectedUser = action.payload
      }
    },

    removeUser: (state, action: PayloadAction<string>) => {
      const userId = action.payload
      state.users = state.users.filter((user) => user.id !== userId)
      state.selectedUsers = state.selectedUsers.filter((id) => id !== userId)
      state.lastOperation = 'delete'
      // Update statistics
      if (state.statistics) {
        state.statistics.totalUsers -= 1
      }
    },

    removeUsers: (state, action: PayloadAction<string[]>) => {
      const userIds = action.payload
      state.users = state.users.filter((user) => !userIds.includes(user.id))
      state.selectedUsers = []
      state.lastOperation = 'bulk_delete'
      // Update statistics
      if (state.statistics) {
        state.statistics.totalUsers -= userIds.length
      }
    },

    updateUserStatus: (
      state,
      action: PayloadAction<{ userIds: string[]; status: AdminUser['status'] }>
    ) => {
      const { userIds, status } = action.payload
      state.users.forEach((user) => {
        if (userIds.includes(user.id)) {
          user.status = status
          user.updated_at = new Date().toISOString()
        }
      })
      state.selectedUsers = []
      state.lastOperation = `bulk_${status}`
    },

    // Cache Management
    updateCacheTimestamp: (state) => {
      state.cacheTimestamp = Date.now()
    },

    setAutoRefresh: (state, action: PayloadAction<boolean>) => {
      state.autoRefresh = action.payload
    },

    // Reset State
    resetState: () => initialState,
  },
})

export const {
  // Data Management
  setUsers,
  setStatistics,
  setPagination,

  // Loading & Error States
  setLoading,
  setError,
  clearError,

  // Filters Management
  setFilters,
  resetFilters,
  setSearchQuery,
  setSortConfig,

  // Selection Management
  setSelectedUsers,
  toggleUserSelection,
  selectAllUsers,
  clearSelection,

  // Modal/Dialog Management
  openCreateDialog,
  closeCreateDialog,
  openEditDialog,
  closeEditDialog,
  openDeleteDialog,
  closeDeleteDialog,
  openBulkDialog,
  closeBulkDialog,
  openDetailsDrawer,
  closeDetailsDrawer,
  openFiltersDrawer,
  closeFiltersDrawer,
  openExportDialog,
  closeExportDialog,

  // Data Operations
  addUser,
  updateUser,
  removeUser,
  removeUsers,
  updateUserStatus,

  // Cache Management
  updateCacheTimestamp,
  setAutoRefresh,

  // Reset State
  resetState,
} = adminUserManagementSlice.actions

// Selectors
export const selectAdminUsers = (state: { adminUserManagement: AdminUserManagementState }) =>
  state.adminUserManagement.users

export const selectAdminUserStatistics = (state: {
  adminUserManagement: AdminUserManagementState
}) => state.adminUserManagement.statistics

export const selectAdminUserFilters = (state: { adminUserManagement: AdminUserManagementState }) =>
  state.adminUserManagement.filters

export const selectAdminUserPagination = (state: {
  adminUserManagement: AdminUserManagementState
}) => state.adminUserManagement.pagination

export const selectAdminUserLoading = (state: { adminUserManagement: AdminUserManagementState }) =>
  state.adminUserManagement.loading

export const selectAdminUserError = (state: { adminUserManagement: AdminUserManagementState }) =>
  state.adminUserManagement.error

export const selectSelectedAdminUsers = (state: {
  adminUserManagement: AdminUserManagementState
}) => state.adminUserManagement.selectedUsers

export const selectSelectedAdminUser = (state: { adminUserManagement: AdminUserManagementState }) =>
  state.adminUserManagement.selectedUser

export const selectEditingAdminUser = (state: { adminUserManagement: AdminUserManagementState }) =>
  state.adminUserManagement.editingUser

// Dialog selectors
export const selectShowCreateDialog = (state: { adminUserManagement: AdminUserManagementState }) =>
  state.adminUserManagement.showCreateDialog

export const selectShowEditDialog = (state: { adminUserManagement: AdminUserManagementState }) =>
  state.adminUserManagement.showEditDialog

export const selectShowDeleteDialog = (state: { adminUserManagement: AdminUserManagementState }) =>
  state.adminUserManagement.showDeleteDialog

export const selectShowBulkDialog = (state: { adminUserManagement: AdminUserManagementState }) =>
  state.adminUserManagement.showBulkDialog

export const selectShowDetailsDrawer = (state: { adminUserManagement: AdminUserManagementState }) =>
  state.adminUserManagement.showDetailsDrawer

export const selectShowFiltersDrawer = (state: { adminUserManagement: AdminUserManagementState }) =>
  state.adminUserManagement.showFiltersDrawer

export const selectShowExportDialog = (state: { adminUserManagement: AdminUserManagementState }) =>
  state.adminUserManagement.showExportDialog

export default adminUserManagementSlice.reducer
